import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { HeaderComponent } from "../../../shared/layout/app/header/header.component";
import { LeftSideNavComponent } from "../../../shared/layout/app/left-menu/left-side-nav.component";
import { ActivatedRoute, Router } from "@angular/router";
import { UserEventStatusRespModel } from "../../profile/profile.model";
import { DeviceTypeService } from "../../../core/services/device-type.service";
import { DigitalCatalogueBllService } from "../digital-catalogue-bll.service";
import { ProfileBllService } from "../../profile/profile-bll.service";
import { UserService } from "../../../core/services/user.service";
import {
  APIErrorModel,
  BaseResponse,
} from "../../../shared/models/base-response.model";
import {
  ParamsToGetProductsList,
  ProductListResponseModel,
} from "../digital-catalogue.model";
import { CommonModule } from "@angular/common";
import { LoaderService } from "../../../shared/components/loader/loader.service";
import { FormsModule } from "@angular/forms";
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { VirtualEventBllService } from "../../virtual-event/virtual-event-bll.service";
import { AuthBllService } from "../../auth/auth-bll.service";
import { HomeBllService } from "../../home/home-bll.service";
import { CatalogueAdsComponent } from "../catalogue-ads/catalogue-ads.component";
import { EventDetailsResponseModel } from "../../home/home.model";

@Component({
  selector: "app-catalogue-product-list",
  imports: [
    HeaderComponent,
    LeftSideNavComponent,
    CommonModule,
    FormsModule,
    InfiniteScrollDirective,
    CatalogueAdsComponent
  ],
  templateUrl: "./catalogue-product-list.component.html",
  styleUrls: ["./catalogue-product-list.component.scss"],
})
export class CatalogueProductListComponent implements OnInit, OnDestroy {
  isMobileDevice: boolean = false;
  companiesCount: number = 0;
  productsCount: number = 0;
  productList: ProductListResponseModel["list"] = [];

  pavilionImgUrl: string = "";
  pavilionName: string = "";

  /** Parameters to fetch product list */
  paramsToFetchData: ParamsToGetProductsList = {
    pavilion_code: "",
    shuffled_index1: 0,
    shuffled_index2: 0,
    max_id: 0,
    currentpage: 1,
    search: "",
  };

  /** pagination properties */
  totalCount = 0;
  pagesCount = 1;
  startItem = 0;
  endItem = 0;
  isDataLoaded = false; // initially false

  searchText: string = "";
  isSearchOpen = false;
  searchDebounce: ReturnType<typeof setTimeout> | null = null;
  @ViewChild("searchInput") searchInput!: ElementRef;

  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;
  categoryName: string = "";
  categoryImgUrl: string = "";
  isLoadingMore: boolean = false;
  isLoading: boolean = false;      // Loader for initial API call
  hasRegisteredAsVirtualVisitor: boolean = false;

   // User authentication status
  isAuthenticated: boolean = false;

  eventDetails: EventDetailsResponseModel | null = null;

  constructor(
    private deviceService: DeviceTypeService,
    private catalogueBllService: DigitalCatalogueBllService,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private router: Router,
    private virtualBll: VirtualEventBllService,
    private authService: AuthBllService,
    private homeBll: HomeBllService
  ) {}

  ngOnInit(): void {
    this.isMobileDevice = this.deviceService.isMobile();

    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    /**
     * Get selected pavilion details from the service
     */
    const pavilionDetails =
      this.catalogueBllService.getSelectedPavilionDetails();
    if (pavilionDetails) {
      this.pavilionImgUrl = pavilionDetails.pavilionImgUrl || "";
      this.pavilionName = pavilionDetails.pavilionName || "";
    }

    const categoryDetails =
      this.catalogueBllService.getSelectedCategoryDetails();
    if (categoryDetails) {
      this.categoryName = categoryDetails.categoryName || "";
      this.categoryImgUrl = categoryDetails.categoryImgUrl || "";
    }

    /**
     * Subscribe to query parameters to get the pavilion code
     */
    this.route.queryParamMap.subscribe((params) => { 
      this.paramsToFetchData.pavilion_code = params.get("pavilion_code") ?? "";
      this.paramsToFetchData.category = params.get("category_id") ?? "";
      this.paramsToFetchData.search = params.get("search") || "";
      this.searchText = this.paramsToFetchData.search || "";
      if (this.paramsToFetchData.pavilion_code) {
        this.callfetchProductList(this.paramsToFetchData);
        this.handlePavilionLoad(this.paramsToFetchData); // Fetch pavilion list on init
      } else if (this.paramsToFetchData.category) {
        this.callfetchProductList(this.paramsToFetchData);
      } else if (this.paramsToFetchData.search) {
        this.callfetchProductList(this.paramsToFetchData);
      } else {
        this.callfetchProductList({ currentpage: 1 }); // Default fetch
      }      
    });

    if (this.isAuthenticated) {
      this.fetchUserEventStatus(); // Fetch user profile on init
    }
    this.getRefSourceFromRoute();
    this.loadEventDetails();
  }

  /**
   * @description Fetches the user's event status and updates the user profile and service accordingly.
   */
  fetchUserEventStatus() {
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
          });
        }

        /**
         * @description Check if the user is an event attendee and register as a virtual visitor if true.
         */
        const attendee = res?.data?.event_my_tickets?.event_attendee_details;
        if (!attendee) {
          this.registerVirtualVisitor();
        }       

      })
      .catch((err: APIErrorModel) => {
        console.error("Error fetching user event status:", err);
      });
  }

  /**
   * Fetch product list from the catalogue
   * @return void
   */
  callfetchProductList(params?: ParamsToGetProductsList, append = false) {
    if (append) {
      this.isLoadingMore = true;  // show spinner for infinite scroll
    } else {
      this.isLoading = true;      // show global loader for first load
      this.loader.show();
    }
    this.catalogueBllService
      .fetchProductList(params)
      .then((res) => {
        const data = res.data;
        this.productList = append ? [...this.productList, ...data.list] : data.list;

        this.startItem = data.startitem;
        this.endItem = data.enditem;
        this.totalCount = data.totalcount;
        this.pagesCount = data.pagescount;
        this.paramsToFetchData.currentpage = data.currentpage;
        this.paramsToFetchData.max_id = data.max_id;
        this.paramsToFetchData.shuffled_index1 = data.shuffled_index1;
        this.paramsToFetchData.shuffled_index2 = data.shuffled_index2;

        this.companiesCount = data.companies_count;
        this.productsCount = data.products_count;
        this.isDataLoaded = true; // set to true after data is loaded

      })
      .catch((err: APIErrorModel) => {
        this.isDataLoaded = true; // set to true even if there's an error
        this.loader.hide(); // stop loader in case of error as well
        if (err.status === 404) {
          this.router.navigate(["/catalogue/pavilion-list"]);
        }
      }).finally(() => {
        // hide loader/spinner depending on call type
        if (append) {
          this.isLoadingMore = false;
        } else {
          this.isLoading = false;
          this.loader.hide();
        }
      });
  }

  /**
   * Navigates to the product details page.
   * @param productId The ID of the product to view.
   */
  viewProductDetails(productId: string | null): void {

    if (!this.isAuthenticated) {
      this.authService.saveStorageValue("state", `/catalogue/product-details/${productId}`);
      this.router.navigate(["/auth/init"]);
    } else {
      this.router.navigate(["/catalogue/product-details", productId]);
    }
    
  }

  /**
   * Handles the page change event.
   * @param page The new page number.
   */
  onPageChange(page: number): void {
    this.loader.show();

    const params = {
      ...this.paramsToFetchData,
      currentpage: page,
    };

    this.callfetchProductList(params);
  }

  /**
   * Routes to companies list with current filters
   */
  routeTocompaniesList(): void {
    const queryParams: any = {};

    if (this.searchText) {
      queryParams.search = this.searchText;
    }

    if (this.paramsToFetchData.category) {
      queryParams.category_id = this.paramsToFetchData.category;
    }

    if (this.paramsToFetchData.pavilion_code) {
      queryParams.pavilion_code = this.paramsToFetchData.pavilion_code;
    }

    this.router.navigate(["/catalogue/companies-list"], { queryParams });
  }

  /**
   * Open floating search
   */
  openFloatingSearch() {
    this.isSearchOpen = true;

    // Focus textbox after animation
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 300);
    }
  }

  /**
   * Close floating search
   */
  closeFloatingSearch() {
    this.isSearchOpen = false;
    this.searchText = "";
    if ("search" in this.paramsToFetchData) {
      delete this.paramsToFetchData.search;
    }

    this.resetParametersToFetchData();
    this.callfetchProductList(this.paramsToFetchData);

    if (
      !this.paramsToFetchData.category &&
      !this.paramsToFetchData.pavilion_code &&
      !this.paramsToFetchData.search
    ) {
      this.router.navigate(["/catalogue/pavilion-list"]);
    }
  }

  openWishlist(): void {
    this.router.navigate(["/catalogue/product-wishlist"]);
  }

  /**
   * Handles search input with debounce
   */
  onSearchInput() {
    const search = this.searchText.trim();

    this.resetParametersToFetchData(search);
    
    // Clear previous timer
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }

    this.searchDebounce = setTimeout(() => {
      this.callfetchProductList(this.paramsToFetchData);
    }, 500); // 500ms debounce
  }

  /**
   * Handles search icon click (immediate search)
   */
  onSearchIconClick() {
    const search = this.searchText.trim();

    this.resetParametersToFetchData(search);

    // Don’t call API if empty search
    if (!search) return;

    this.callfetchProductList(this.paramsToFetchData);
    // Clear debounce so API is not triggered twice
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  }

  /**
   * Scroll to top of the page
   */
  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
  /**
   * clear the debounce timer on component destroy
   */
  ngOnDestroy(): void {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
  }

  // Triggered when user scrolls down
  onScrollDown() {
    // Prevent multiple simultaneous calls
    if (this.isLoading || this.isLoadingMore) return;

    if (!this.paramsToFetchData.currentpage) return;

    // Only call API if there are more pages
    if (this.paramsToFetchData.currentpage < this.pagesCount) {
      const nextParams: ParamsToGetProductsList = {
        ...this.paramsToFetchData,
        currentpage: this.paramsToFetchData.currentpage + 1,
        shuffled_index1: this.paramsToFetchData.shuffled_index1,
        shuffled_index2: this.paramsToFetchData.shuffled_index2,
      };

      // Use load more spinner for infinite scroll
      this.callfetchProductList(nextParams, true); // append items
    }
  }

  onClickBack() {
    if (
      this.paramsToFetchData.category ||
      this.route.snapshot.queryParamMap.get("tab") === "areaofinterest"
    ) {
      this.router.navigate(["catalogue/pavilion-list"], {
        queryParams: { tab: "areaofinterest" },
      });
    } else {
      this.router.navigate(["catalogue/pavilion-list"]);
    }
  }

  /**
   * function to open layout view
   */
  viewLayout(){
    this.catalogueBllService.openLayout();
  }

  /**
   * function to handle pavilion load and set pavilion details
   * @param params 
   * @returns 
   */
  handlePavilionLoad(params?: ParamsToGetProductsList) {
    const stored = this.catalogueBllService.getSelectedPavilionDetails();

    // If stored pavilion matches the code → use it
    if (stored && stored.pavilionCode === params?.pavilion_code) {
      this.pavilionName = stored.pavilionName || "";
      this.pavilionImgUrl = stored.pavilionImgUrl || "";
      return;
    }

    // Otherwise fetch and store
    this.catalogueBllService.fetchPavilionList()
      .then(res => {
        const allPavilions = res.data.pavilion_groups.flatMap(
          group => group.pavilions
        );

        const pavilion = allPavilions.find(
          p => p.code?.toLowerCase() === params?.pavilion_code?.toLowerCase()
        );

        if (!pavilion) return; 

        const data = {
          pavilionCode: params?.pavilion_code,
          pavilionName: pavilion.name || "",
          pavilionImgUrl: pavilion.pavilion_hover_image_url || ""
        };

        // Set in UI
        this.pavilionName = data.pavilionName;
        this.pavilionImgUrl = data.pavilionImgUrl;

      })
      .catch(() => {
      });
  }

  goToTicketPage(){
    this.router.navigate(["reg/select-ticket"]);
  }
  
  registerVirtualVisitor() {
    this.virtualBll.submitRegisterVirtualVisitor().then(() => {
      // Registration successful
      this.hasRegisteredAsVirtualVisitor = true;
    }).catch(() => {
      // Registration failed or already registered
      // We can choose to ignore errors here
      this.hasRegisteredAsVirtualVisitor = false;
    });
  }

  getRefSourceFromRoute() {
    const refSource =
      this.route.snapshot.queryParamMap.get("ref_source")|| '';
    if (!refSource) {
      console.error("Ref source not found in route parameters");

    }
    this.homeBll.homeInitStorageChanges(undefined,refSource);
  }

  // reset parameters to fetch data
  resetParametersToFetchData(search?: string) {
    this.paramsToFetchData = {
      pavilion_code: this.paramsToFetchData.pavilion_code,
      shuffled_index1: 0,
      shuffled_index2: 0,
      max_id: 0,
      currentpage: 1,
      search: search,
      category: this.paramsToFetchData.category
    }
  }

  loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;      
      
         this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });
        
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}
